import { useState } from "react";
import ButtonGroup from "./ButtonGroup";

## Basic Usage

<ButtonGroup
  size="medium"
  items={[
    { children: "Option 1", isActive: true, onClick: () => {} },
    { children: "Option 2", isActive: false, onClick: () => {} },
    { children: "Option 3", isActive: false, onClick: () => {} },
  ]}
/>

---

## Interactive Example

export function InteractiveButtonGroup() {
  const [activeIndex, setActiveIndex] = useState(0);
  const options = ["Day", "Week", "Month", "Year"];
  return (
    <ButtonGroup
      size="medium"
      items={options.map((label, i) => ({
        children: label,
        isActive: activeIndex === i,
        onClick: () => setActiveIndex(i),
      }))}
    />
  );
}

<InteractiveButtonGroup />

---

## Sizes

<div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
  <div>
    <p style={{ marginBottom: "0.5rem" }}>**Small**</p>
    <ButtonGroup
      size="small"
      items={[
        { children: "A", isActive: true, onClick: () => {} },
        { children: "B", isActive: false, onClick: () => {} },
        { children: "C", isActive: false, onClick: () => {} },
      ]}
    />
  </div>
  <div>
    <p style={{ marginBottom: "0.5rem" }}>**Medium**</p>
    <ButtonGroup
      size="medium"
      items={[
        { children: "A", isActive: true, onClick: () => {} },
        { children: "B", isActive: false, onClick: () => {} },
        { children: "C", isActive: false, onClick: () => {} },
      ]}
    />
  </div>
  <div>
    <p style={{ marginBottom: "0.5rem" }}>**Large**</p>
    <ButtonGroup
      size="large"
      items={[
        { children: "A", isActive: true, onClick: () => {} },
        { children: "B", isActive: false, onClick: () => {} },
        { children: "C", isActive: false, onClick: () => {} },
      ]}
    />
  </div>
</div>

---

## Custom Button Styles

<div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
  <div>
    <p style={{ marginBottom: "0.5rem" }}>**Default (primary/tertiary)**</p>
    <ButtonGroup
      size="medium"
      items={[
        { children: "Active", isActive: true, onClick: () => {} },
        { children: "Inactive", isActive: false, onClick: () => {} },
      ]}
    />
  </div>
  <div>
    <p style={{ marginBottom: "0.5rem" }}>**Secondary active style**</p>
    <ButtonGroup
      size="medium"
      activeBtnStyle="secondary"
      items={[
        { children: "Active", isActive: true, onClick: () => {} },
        { children: "Inactive", isActive: false, onClick: () => {} },
      ]}
    />
  </div>
  <div>
    <p style={{ marginBottom: "0.5rem" }}>**Green active style**</p>
    <ButtonGroup
      size="medium"
      activeBtnStyle="green"
      items={[
        { children: "Active", isActive: true, onClick: () => {} },
        { children: "Inactive", isActive: false, onClick: () => {} },
      ]}
    />
  </div>
</div>

---

## Multiple Active States

export function MultipleActiveStates() {
  const [selected, setSelected] = useState(new Set([0, 2]));
  const options = ["Bold", "Italic", "Underline"];
  const toggle = (i) => {
    const next = new Set(selected);
    if (next.has(i)) next.delete(i);
    else next.add(i);
    setSelected(next);
  };
  return (
    <div>
      <ButtonGroup
        size="medium"
        items={options.map((label, i) => ({
          children: label,
          isActive: selected.has(i),
          onClick: () => toggle(i),
        }))}
      />
      <p>Selected: **{options.filter((_, i) => selected.has(i)).join(", ") || "none"}**</p>
    </div>
  );
}

<MultipleActiveStates />
